// ONE implementation, because settle DETECTION is what is easy to get subtly wrong: `scrollend` is the correct
// signal but is not everywhere, so a debounce fallback exists and both paths must agree about a held finger.
// Part of the engine's port set — route-transition/surface/scroll-transition-outlet.tsx is kept byte-identical
// with the sibling repo and imports this, so an engine sync has to carry this file too.

// Axis-agnostic: the stack passes scrollLeft, the sheet scrollTop.
export function snapProgress(scrolled: number, maxScroll: number): number {
  if (maxScroll <= 0) return 0
  const progress = 1 - scrolled / maxScroll
  return progress < 0 ? 0 : progress > 1 ? 1 : progress
}

// Not a threshold for the user's decision — mandatory snap already rests ON a slot; this absorbs a creeping momentum tail.
export const SNAP_LANDED = 0.98

/** How long after the last scroll event a sequence counts as settled, where `scrollend` isn't implemented. */
export const SNAP_SETTLE_DEBOUNCE_MS = 140

// A scroller that never reaches a slot would otherwise re-arm the debounce forever.
const SNAP_RETRY_LIMIT = 20

export interface SnapScrollerHandlers {
  // Tell a position the surface caused itself apart from a gesture by POSITION, never by a timer.
  owns?: () => boolean
  /** The first frame of a scroll the gesture owns — where the surface switches into its dragging state. */
  onStart?: () => void
  /** Every rAF while it moves. Keep this to READS plus cheap writes; it is the frame path. */
  onFrame: () => void
  // Settled means POSITION, not silence: on the debounce path a long task freezes a snap animation and its scroll
  // events together, so silence can mean the opposite of settled. Anything but `false` is taken as arrived.
  onSettle: (wasScrolling: boolean) => boolean | void
}

/** Live state of an observed scroller, for the surfaces that need to ask rather than track it themselves. */
export interface SnapScrollerHandle {
  /** Stop observing. */
  stop: () => void
  /** Is a finger down on this scroller right now? */
  held: () => boolean
  // Lets a consumer act on a position that already answers what silence is about to — the wait IS a back-swipe's
  // felt latency (measured on iPhone: `scrollend` arriving 0-509ms after the last scroll event).
  // Without it the late announcement reports a landing already spent, and the next drag gets no onStart.
  consume: () => void
}

// Coalesced to one onFrame per animation frame, so a starved frame lags a derived cue rather than stalling the gesture.
export function observeSnapScroller(el: HTMLElement, handlers: SnapScrollerHandlers): SnapScrollerHandle {
  let frame = 0
  let settleTimer = 0
  let scrolling = false
  let retries = 0
  // Nothing settles while a finger is down: the debounce fires on a drag stalled under a stationary finger, and
  // `scrollend` can arrive with the touch still down when the finger has left the scroller's box.
  // TOUCH events, not pointer: the browser fires pointercancel the instant a native scroll claims the gesture.
  let held = false
  /** A settle that arrived while the finger was down. Owed to the release, which re-arms the debounce for it. */
  let owed = false
  // A settle under a held finger is the browser announcing its own snap-back, which the finger then yanks against
  // and the surface oscillates — so snap goes off from that moment until the touch ends.
  // Restoring it on release IS the snap-back: the restyle makes the browser animate to the nearest slot.
  let snapSuppressed = false

  const suppressSnap = () => {
    if (snapSuppressed) return
    snapSuppressed = true
    el.style.scrollSnapType = 'none'
  }

  const restoreSnap = () => {
    if (!snapSuppressed) return
    snapSuppressed = false
    el.style.scrollSnapType = ''
  }
  // Also the backstop, on `scrollend` engines, for a settle refused under a held finger.
  const supportsScrollEnd = 'onscrollend' in window

  const publish = () => {
    frame = 0
    handlers.onFrame()
  }

  const armDebounce = () => {
    clearTimeout(settleTimer)
    settleTimer = window.setTimeout(settle, SNAP_SETTLE_DEBOUNCE_MS)
  }

  function settle() {
    clearTimeout(settleTimer)
    settleTimer = 0
    if (held) {
      owed = true
      suppressSnap()
      return
    }
    owed = false
    const was = scrolling
    scrolling = false
    if (handlers.onSettle(was) === false && was && ++retries < SNAP_RETRY_LIMIT) {
      // `scrollend` will not fire twice for one scroll, so this path is the debounce's alone.
      scrolling = true
      armDebounce()
      return
    }
    retries = 0
  }

  const onScroll = () => {
    if (handlers.owns && !handlers.owns()) return
    if (!scrolling) {
      scrolling = true
      retries = 0
      handlers.onStart?.()
    }
    if (!frame) frame = requestAnimationFrame(publish)
    // Re-armed every frame while it is the deciding signal, so it can only fire once the scroller has stopped.
    if (!supportsScrollEnd || owed) armDebounce()
  }

  const onTouchStart = () => {
    held = true
    owed = false
    // A fresh gesture supersedes whatever the last one was still counting down to.
    clearTimeout(settleTimer)
    settleTimer = 0
  }

  const onTouchEnd = (event: TouchEvent) => {
    if (event.touches.length > 0) return // another finger is still down; the gesture isn't over
    if (!held) return
    held = false
    // Before the debounce is armed: the restyle starts the browser's snap-back, whose events then re-arm that clock.
    restoreSnap()
    // The release is where momentum and snap BEGIN, so a timer armed here would read a half-finished position.
    if (owed) armDebounce()
  }

  el.addEventListener('scroll', onScroll, { passive: true })
  el.addEventListener('touchstart', onTouchStart, { passive: true })
  window.addEventListener('touchend', onTouchEnd, { passive: true })
  window.addEventListener('touchcancel', onTouchEnd, { passive: true })
  if (supportsScrollEnd) el.addEventListener('scrollend', settle)

  return {
    held: () => held,
    consume: () => {
      clearTimeout(settleTimer)
      settleTimer = 0
      owed = false
      scrolling = false
      retries = 0
    },
    stop: () => {
      if (frame) cancelAnimationFrame(frame)
      clearTimeout(settleTimer)
      restoreSnap()
      el.removeEventListener('scroll', onScroll)
      el.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchend', onTouchEnd)
      window.removeEventListener('touchcancel', onTouchEnd)
      if (supportsScrollEnd) el.removeEventListener('scrollend', settle)
    },
  }
}
